/**
 * Filler people put in front of a command that carries no meaning here.
 *
 * "add preferred venues" returned nothing before this, because every token had
 * to appear somewhere in the item and "add" appears nowhere. Dropping filler is
 * what lets someone type the way they speak.
 *
 * The question words earn their place the same way: "get help" found nothing
 * while "help" found support, and "how do i compare" found nothing at all,
 * which is how half the team phrases a search.
 */
const STOPWORDS = new Set([
  "a",
  "add",
  "an",
  "can",
  "create",
  "do",
  "does",
  "find",
  "for",
  "get",
  "go",
  "how",
  "i",
  "make",
  "me",
  "my",
  "need",
  "new",
  "on",
  "open",
  "our",
  "please",
  "see",
  "show",
  "the",
  "to",
  "view",
  "want",
  "where",
]);

/**
 * Edit distance we tolerate, by token length. Short words get none: at three
 * letters almost everything is one edit from everything else.
 *
 * @param token - The typed word.
 * @returns How many edits a match may be away (0 means exact only).
 */
export function allowedDistance(token: string): number {
  if (token.length <= 3) return 0;
  if (token.length <= 6) return 1;
  return 2;
}

/**
 * Levenshtein distance, abandoned early once it exceeds `max`.
 *
 * @param a - First string.
 * @param b - Second string.
 * @param max - Distance beyond which the exact value stops mattering.
 * @returns The distance, or a value greater than `max`.
 */
export function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
      rowBest = Math.min(rowBest, current[j]);
    }
    if (rowBest > max) return max + 1;
    previous = current;
  }
  return previous[b.length];
}

/** Splits searchable text into words, keeping `&` so "f&b" survives. */
export const toWords = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^a-z0-9&]+/)
    .filter(Boolean);

/**
 * Strips filler from a query, keeping the original tokens when filler is all
 * there was (so "new" alone can still find "New event").
 *
 * @param query - The raw query.
 * @returns Meaningful lowercase tokens.
 */
export function meaningfulTokens(query: string): string[] {
  const tokens = toWords(query);
  const kept = tokens.filter((token) => !STOPWORDS.has(token));
  return kept.length > 0 ? kept : tokens;
}

/**
 * Whether a token matches any of an item's words.
 *
 * Substring first, so a prefix like "prop" still finds "proposals", then a
 * bounded edit distance so "proposls" and "contarcts" do too. Admission used to
 * be substring-only, which meant a single typo returned nothing at all.
 *
 * @param words - The item's searchable words.
 * @param token - One query token.
 * @returns true when the token matches.
 */
export function tokenMatches(words: string[], token: string): boolean {
  if (words.some((word) => word.includes(token))) return true;
  const max = allowedDistance(token);
  if (max === 0) return false;
  return words.some((word) => editDistance(token, word, max) <= max);
}
